import { StandardEvents } from '@shopify/events';
import {
  ENERGY_ATTRIBUTE_KEYS,
  ENERGY_INTERESTS,
  buildEnergyUrl,
  persistEnergyCartAttributes,
} from '@apokto/commerce-utils';

class ApoktoEnergyCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', this.onClick, { signal });

    if (this.dataset.context === 'product') {
      this.section = this.closest('.shopify-section, dialog');
      this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect, { signal });
      this.updateProductLink(this.dataset.variantId);
    }

    if (this.dataset.context === 'cart') {
      this.updateCartUi(this.dataset.selected || '');
      this.reconcileCartMetadata();
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  onClick = (event) => {
    const choice = event.target.closest('[data-energy-choice]');
    if (choice && this.contains(choice)) {
      this.persistInterest(choice.dataset.energyChoice);
      return;
    }
    const clear = event.target.closest('[data-energy-clear]');
    if (clear && this.contains(clear)) this.persistInterest('');
  };

  onProductSelect = (event) => {
    event.promise?.then(({ detail }) => {
      if (detail?.resource?.id) this.updateProductLink(String(detail.resource.id));
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[apokto-energy] Variant update failed:', error);
    });
  };

  updateProductLink(variantId) {
    const link = this.querySelector('[data-energy-cta]');
    if (!link) return;
    this.dataset.variantId = variantId || '';
    link.href = buildEnergyUrl(this.dataset.energyBaseUrl, {
      source: 'product',
      category: this.dataset.originCategory,
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
    });
  }

  get cartContext() {
    return {
      category: this.dataset.originCategory || '',
      productIds: (this.dataset.productIds || '').split(',').filter(Boolean),
      variantIds: (this.dataset.variantIds || '').split(',').filter(Boolean),
    };
  }

  currentAttributesMatch() {
    const context = this.cartContext;
    return ['quiz', 'cart'].includes(this.dataset.currentSource) &&
      this.dataset.currentCategory === context.category &&
      this.dataset.currentProductIds === context.productIds.join(',') &&
      this.dataset.currentVariantIds === context.variantIds.join(',');
  }

  reconcileCartMetadata() {
    const selected = ENERGY_INTERESTS.includes(this.dataset.selected) ? this.dataset.selected : '';
    if (!selected) return;
    const source = ['quiz', 'cart'].includes(this.dataset.currentSource) ? this.dataset.currentSource : 'cart';
    if (this.dataset.eligible !== 'true') {
      this.persistInterest('', { quiet: true, source });
    } else if (!this.currentAttributesMatch()) {
      this.persistInterest(selected, { quiet: true, source });
    }
  }

  updateCartUi(selected) {
    this.dataset.selected = selected;
    for (const button of this.querySelectorAll('[data-energy-choice]')) {
      const active = button.dataset.energyChoice === selected;
      button.setAttribute('aria-pressed', String(active));
      button.querySelector('[data-energy-selected-label]')?.toggleAttribute('hidden', !active);
    }
    this.querySelector('[data-energy-clear]')?.toggleAttribute('hidden', !selected);
    const link = this.querySelector('[data-energy-cta]');
    if (link) {
      link.toggleAttribute('hidden', !selected);
      if (selected) {
        link.href = buildEnergyUrl(this.dataset.energyBaseUrl, {
          interest: selected,
          source: 'cart',
          category: this.dataset.originCategory,
        });
      }
    }
  }

  get cartComponents() {
    return [...document.querySelectorAll("apokto-energy-cross-sell[data-context='cart']")];
  }

  applyCurrentAttributes(attributes) {
    this.dataset.currentSource = attributes[ENERGY_ATTRIBUTE_KEYS.source] || '';
    this.dataset.currentCategory = attributes[ENERGY_ATTRIBUTE_KEYS.category] || '';
    this.dataset.currentProductIds = attributes[ENERGY_ATTRIBUTE_KEYS.productIds] || '';
    this.dataset.currentVariantIds = attributes[ENERGY_ATTRIBUTE_KEYS.variantIds] || '';
  }

  snapshot() {
    return {
      selected: this.dataset.selected || '',
      currentSource: this.dataset.currentSource || '',
      currentCategory: this.dataset.currentCategory || '',
      currentProductIds: this.dataset.currentProductIds || '',
      currentVariantIds: this.dataset.currentVariantIds || '',
    };
  }

  restore(snapshot) {
    this.updateCartUi(snapshot.selected);
    this.dataset.currentSource = snapshot.currentSource;
    this.dataset.currentCategory = snapshot.currentCategory;
    this.dataset.currentProductIds = snapshot.currentProductIds;
    this.dataset.currentVariantIds = snapshot.currentVariantIds;
  }

  async persistInterest(interest, { quiet = false, source = 'cart' } = {}) {
    const selected = ENERGY_INTERESTS.includes(interest) ? interest : '';
    if (this.busy || selected === this.dataset.selected && this.currentAttributesMatch()) return;
    const components = this.cartComponents;
    if (components.some((component) => component.busy)) return;
    const snapshots = components.map((component) => [component, component.snapshot()]);
    const status = this.querySelector('[data-energy-status]');
    for (const component of components) {
      component.busy = true;
      component.setAttribute('aria-busy', 'true');
      component.updateCartUi(selected);
    }
    if (!quiet && status) status.textContent = selected ? 'Salvataggio interesse Energia…' : 'Rimozione interesse Energia…';

    try {
      const { attributes } = await persistEnergyCartAttributes(
        Theme.routes.cart_update_url,
        selected,
        { ...this.cartContext, source }
      );
      for (const component of components) component.applyCurrentAttributes(attributes);
      if (!quiet && status) status.textContent = selected ? 'Interesse Energia salvato.' : 'Interesse Energia rimosso.';
    } catch (error) {
      for (const [component, snapshot] of snapshots) component.restore(snapshot);
      if (status) {
        status.textContent = 'Non è stato possibile salvare la preferenza. Puoi comunque procedere al checkout.';
        status.setAttribute('tabindex', '-1');
        status.focus();
      }
      console.warn('[apokto-energy] Cart attribute update failed:', error);
    } finally {
      for (const component of components) {
        component.busy = false;
        component.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('apokto-energy-cross-sell')) {
  customElements.define('apokto-energy-cross-sell', ApoktoEnergyCrossSell);
}
